import { ApiPaths } from "../../api/ApiPaths";
import { CareRecipientId } from "../../../domain/care_recipient/CareRecipientId";
import { Month } from "../../../domain/fundamental/Month";
import { DateObject } from "../../../domain/fundamental/DateObject";
import { CommentCategory } from "../../../domain/report/CommentCategory";
import { ApiRequest } from "../../api/ApiRequest";
import { DashBoardGraphCategory } from "./reducer";
import { Average } from "../../../domain/report/average/Average";

export type GetDailyCommentsSuccessResponseType = {
    dailyAlertCommentList: { careRecipientId: string, date: string, category: string, message: string }[],
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
}

export async function getDailyComments(
    careRecipientId: CareRecipientId,
    date?: DateObject
): Promise<GetDailyCommentsSuccessResponseType> {
    let params = {};
    if (date !== undefined) {
        params = {
            care_recipient_id: careRecipientId.value,
            date: date.format()
        };
    } else {
        params = {
            care_recipient_id: careRecipientId.value
        };
    }

    return await ApiRequest.get(
        ApiPaths.report.dailyComments,
        params,
        () => {},
        () => {});
}

export type GetMonthlyCommentsSuccessResponseType = {
    medicalComments: { comment: string, emotion: string, month: string, category: CommentCategory, averageData: number }[],
    statisticalComments: { comment: string, month: string, category: CommentCategory, averageData: number }[],
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
}

export async function getMonthlyComments(
    careRecipientId: CareRecipientId,
    month: Month
): Promise<GetMonthlyCommentsSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.report.monthlyComments,
        {
            care_recipient_id: careRecipientId.value,
            month: month.format()
        },
        () => {},
        () => {});
}

export type GetDailyEstimatedBehaviorSuccessResponseType = {
    estimatedBehavior: {
        sleep: { start: string, end: string }[],
        bath: { start: string, end: string }[],
        toilet: { start: string, end: string }[],
        meal: { start: string, end: string }[],
        sweeping: { start: string, end: string }[],
        laundry: { start: string, end: string }[],
        doorOpening: { start: string, end: string }[],
        tv: { start: string, end: string }[],
        airConditioner: { start: string, end: string }[],
        goingOut: { start: string, end: string }[],
        regularGoingOut: { start: string, end: string }[]
    },
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
}

export type GetCareRecipientLatestInfoSuccessResponseType = {
    informationList: {
        careRecipientId: string,
        estimatedBehaviorLatest: string | null,
        haveDailyAlertComment: boolean,
        haveUnreadCareComment: boolean
    }[],
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
}

export async function getDailyEstimatedBehavior(
    careRecipientId: CareRecipientId,
    date: DateObject
): Promise<GetDailyEstimatedBehaviorSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.report.dailyEstimatedBehaviors,
        {
            care_recipient_id: careRecipientId.value,
            date: date.format()
        },
        () => {},
        () => {});
}

export async function getCareRecipientLatestInfo(): Promise<GetCareRecipientLatestInfoSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.report.careRecipientLatestInfo,
        {},
        () => {},
        () => {});
}

type TimesHistory = {
    date: string,
    times: number
}
type DevidedTimesHistory = {
    date: string,
    dailyTimes: number,
    nightTimes: number
}
type TotalMinuteHistory = {
    date: string,
    totalMinute: number
}
type StartEndHistory = {
    date: string,
    startTime: string,
    endTime: string
}
export type GetEstimatedBehaviorHistoriesSuccessResponseType = {
    histories: {
        toilet: DevidedTimesHistory[],
        bath: TimesHistory[],
        goingOut: TimesHistory[],
        sleep: TotalMinuteHistory[],
        meal: StartEndHistory[]
    },
    // API処理結果判別用：OK->undefined、NG->ErrorResponseType
    error: undefined
}

export async function getEstimatedBehaviorHistories(
    careRecipientId: CareRecipientId,
    startDate: DateObject,
    endDate: DateObject
): Promise<GetEstimatedBehaviorHistoriesSuccessResponseType> {
    return await ApiRequest.get(
        ApiPaths.report.estimatedBehaviorHistories.replace(/{careRecipientId}/g, careRecipientId.value),
        { start_date: startDate.format(), end_date: endDate.format() },
        () => {},
        () => {}
    );
}

export type GetEstimatedBehaviorAveragesType = Average & { error: undefined }

export async function getEstimatedBehaviorAverages(
    careRecipientId: CareRecipientId
): Promise<GetEstimatedBehaviorAveragesType> {
    return await ApiRequest.get(
        ApiPaths.report.estimatedBehaviorAverages.replace(/{careRecipientId}/g, careRecipientId.value),
        () => {},
        () => {}
    );
}

export type GetEstimatedBehaviorAlertType = {
    alerts: {
        careRecipientId: string,
        date: string,
        message: string,
        category: string
    }[],
    error: undefined
}

export async function getEstimatedBehaviorAlert(
    careRecipientId: CareRecipientId,
    date: DateObject
): Promise<GetEstimatedBehaviorAlertType> {
    return await ApiRequest.get(
        ApiPaths.report.estimatedBehaviorAlert.replace(/{careRecipientId}/g, careRecipientId.value),
        { date: date.format() },
        () => {},
        () => {}
    );
}

export type GetEstimatedBehaviorAlertPeriodType = {
    alerts: {
        [category in DashBoardGraphCategory]: {
            [date: string]: string[]
        }
    },
    error: undefined
}

export async function getEstimatedBehaviorAlertPeriod(
    careRecipientId: CareRecipientId,
    startDate: DateObject,
    endDate: DateObject
): Promise<GetEstimatedBehaviorAlertPeriodType> {
    return await ApiRequest.get(
        ApiPaths.report.estimatedBehaviorAlertPeriod.replace(/{careRecipientId}/g, careRecipientId.value),
        { start_date: startDate.format(), end_date: endDate.format() },
        () => {},
        () => {}
    );
}
